import React, { useEffect, useState } from 'react';
import { apiFetch } from '../utils/apiAdmin';
import { AuditEventList } from './AuditEntityPanel';

/**
 * One task's own recorded history, inside TaskDetailsModal (Part F of
 * Planning/AUDIT-PAGE-2026-08-16.md).
 *
 * ⚠️ ADMIN PORTAL ONLY, AND DELIBERATELY. TaskDetailsModal is shared with the
 * client portal, and the trail carries the text of soft-deleted comments and
 * colleagues' activity. There is no client equivalent of this and there must not
 * be one - the same ruling that keeps the history tools out of the client
 * connector (D8).
 *
 * ⚠️ IT RENDERS AuditEventList, NOT ITS OWN LIST. The audit page's slide-over
 * shows the same thing, and a second copy would drift - most likely in the
 * raw-log warning, which is the part that stops an event count being read as a
 * change count.
 *
 * `include=changes`: the access family is a project-wide question and belongs on
 * the audit page, not hanging off one task.
 */
function TaskHistory({ projectId, taskId }) {
    const [state, setState] = useState({ loading: true, events: [] });

    useEffect(() => {
        if (!projectId || !taskId) return undefined;

        let cancelled = false;
        setState({ loading: true, events: [] });

        (async () => {
            try {
                const params = new URLSearchParams({
                    entityId: taskId,
                    include: 'changes',
                    limit: '500'
                });
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/audit/${encodeURIComponent(projectId)}?${params}`
                );
                const body = await response.json().catch(() => ({}));
                if (cancelled) return;

                setState(response.ok
                    ? {
                        loading: false,
                        events: body.entries || [],
                        // The bound travels with the answer: a task with more
                        // than one page of history must not look like it has one.
                        hasMore: Boolean(body.hasMore)
                    }
                    : { loading: false, events: [], error: body.error || `History unavailable (${response.status})` });
            } catch (err) {
                if (!cancelled) setState({ loading: false, events: [], error: err.message });
            }
        })();

        return () => { cancelled = true; };
    }, [projectId, taskId]);

    if (state.loading) return <p className="text-xs text-gray-500">Reading the trail…</p>;
    if (state.error) return <p className="text-xs text-red-700">{state.error}</p>;

    return (
        <div>
            <AuditEventList
                events={state.events}
                windowLabel={state.hasMore ? 'the most recent 500 events' : 'this task\'s whole trail'}
            />
            {state.hasMore && (
                <p className="mt-2 text-xs text-amber-700">
                    Only the most recent 500 events were read — there are older ones.
                    The Audit Trail page can page through the rest.
                </p>
            )}
        </div>
    );
}

export default TaskHistory;
